import React from 'react';
import { Icon, IconProps } from '../../icons/Icon';

import './FormSection.css';

// ============================================
// FormSection Props
// ============================================
export interface FormSectionProps {
  /** Section title */
  title?: string;
  /** Section description */
  description?: string;
  /** Icon to show next to title */
  icon?: IconProps['name'];
  /** Whether section is collapsible */
  collapsible?: boolean;
  /** Default collapsed state (only if collapsible) */
  defaultCollapsed?: boolean;
  /** Controlled collapsed state */
  collapsed?: boolean;
  /** Callback when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Action buttons/links for header */
  actions?: React.ReactNode;
  /** Form fields/content */
  children: React.ReactNode;
  /** Render as a card with border */
  card?: boolean;
  /** Additional CSS class */
  className?: string;
}

export function FormSection({
  title,
  description,
  icon,
  collapsible = false,
  defaultCollapsed = false,
  collapsed: controlledCollapsed,
  onCollapsedChange,
  actions,
  children,
  card = true,
  className = '',
}: FormSectionProps) {
  const [internalCollapsed, setInternalCollapsed] = React.useState(defaultCollapsed);

  const isControlled = controlledCollapsed !== undefined;
  const isCollapsed = isControlled ? controlledCollapsed : internalCollapsed;

  const handleToggle = () => {
    if (!collapsible) return;

    const newValue = !isCollapsed;
    if (isControlled) {
      onCollapsedChange?.(newValue);
    } else {
      setInternalCollapsed(newValue);
      onCollapsedChange?.(newValue);
    }
  };

  const hasHeader = title || description || actions;

  return (
    <section
      className={`form-section ${card ? 'form-section--card' : ''} ${collapsible ? 'form-section--collapsible' : ''} ${isCollapsed ? 'form-section--collapsed' : ''} ${className}`.trim()}
    >
      {hasHeader && (
        <div
          className="form-section__header"
          onClick={collapsible ? handleToggle : undefined}
          role={collapsible ? 'button' : undefined}
          aria-expanded={collapsible ? !isCollapsed : undefined}
          tabIndex={collapsible ? 0 : undefined}
          onKeyDown={
            collapsible
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleToggle();
                  }
                }
              : undefined
          }
        >
          <div className="form-section__header-content">
            {icon && (
              <span className="form-section__icon">
                <Icon name={icon} size={20} />
              </span>
            )}
            <div className="form-section__titles">
              {title && <h3 className="form-section__title">{title}</h3>}
              {description && <p className="form-section__description">{description}</p>}
            </div>
          </div>

          <div className="form-section__header-actions">
            {actions && <div className="form-section__actions">{actions}</div>}
            {collapsible && (
              <span className="form-section__collapse-icon">
                <Icon name="ChevronDown" size={20} />
              </span>
            )}
          </div>
        </div>
      )}

      <div className="form-section__body">
        {children}
      </div>
    </section>
  );
}

// ============================================
// FormGroup - For grouping form fields with label
// ============================================
export interface FormGroupProps {
  /** Field label */
  label?: string;
  /** Whether field is required */
  required?: boolean;
  /** Help text below field */
  helpText?: string;
  /** Error message */
  error?: string;
  /** Form field(s) */
  children: React.ReactNode;
  /** Additional CSS class */
  className?: string;
}

export function FormGroup({
  label,
  required = false,
  helpText,
  error,
  children,
  className = '',
}: FormGroupProps) {
  return (
    <div className={`form-group ${error ? 'form-group--error' : ''} ${className}`.trim()}>
      {label && (
        <label className={`form-group__label ${required ? 'form-group__label--required' : ''}`}>
          {label}
        </label>
      )}
      <div className="form-group__field">{children}</div>
      {helpText && !error && <p className="form-group__help">{helpText}</p>}
      {error && <p className="form-group__error">{error}</p>}
    </div>
  );
}

// ============================================
// FormRow - For horizontal field layouts
// ============================================
export interface FormRowProps {
  /** Form fields */
  children: React.ReactNode;
  /** Gap between fields */
  gap?: 'sm' | 'md' | 'lg';
  /** Stack on mobile */
  stackOnMobile?: boolean;
  /** Additional CSS class */
  className?: string;
}

export function FormRow({
  children,
  gap = 'md',
  stackOnMobile = true,
  className = '',
}: FormRowProps) {
  return (
    <div
      className={`form-row form-row--gap-${gap} ${stackOnMobile ? 'form-row--stack-mobile' : ''} ${className}`.trim()}
    >
      {children}
    </div>
  );
}

// ============================================
// FormDivider - Visual separator
// ============================================
export interface FormDividerProps {
  /** Optional label */
  label?: string;
}

export function FormDivider({ label }: FormDividerProps) {
  return (
    <div className="form-divider">
      {label && <span className="form-divider__label">{label}</span>}
    </div>
  );
}

// Attach subcomponents
FormSection.Group = FormGroup;
FormSection.Row = FormRow;
FormSection.Divider = FormDivider;
